import * as React from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tab,
  Tabs,
} from '@mui/material';

import ClientServicesTab from './ClientServicesTab';
import { z } from 'zod';
import { notifyAPIClient } from '../../utils/toast';
import { apiPost } from '../../utils/apiClient';
import { useIsMobile } from '../../hooks/useResponsive';
import { User, Gender } from '../../types/RegistrationTable.type';

type UserInfoDialogProps = {
  open: boolean;
  handleClose: () => void;
  user: User | null;
  fetchUsers: () => void;
  /** When true the dialog is a read-only "view profile" (no editing/saving). */
  viewOnly?: boolean;
};

type FormErrors = {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  businessName?: string;
  email?: string;
  phone?: string;
  parent?: string;
  address?: string;
  idCard?: string;
  taxCode?: string;
  dob?: string;
  gender?: string;
};

const UserInfoDialog = ({
  open,
  handleClose,
  user,
  fetchUsers,
  viewOnly = false,
}: UserInfoDialogProps) => {
  const isMobile = useIsMobile();
  const [tab, setTab] = React.useState<'profile' | 'services'>('profile');
  // A view-only dialog can be switched into edit mode via the Edit button.
  const [editMode, setEditMode] = React.useState(false);
  const readOnly = viewOnly && !editMode;

  // Always land on the Profile tab (and reset edit mode) when the dialog reopens.
  React.useEffect(() => {
    if (open) {
      setTab('profile');
      setEditMode(false);
    }
  }, [open]);

  const UserInfoInput = z.object({
    id: z.number().int(),
    firstName: z
      .string()
      .min(1, 'Please enter first name')
      .refine((val) => val.length === 0 || val.trim().length > 0, {
        message: 'First name cannot be just whitespace',
      }),
    lastName: z
      .string()
      .min(1, 'Please enter last name')
      .refine((val) => val.length === 0 || val.trim().length > 0, {
        message: 'Last name cannot be just whitespace',
      }),
    preferredName: z.string().optional().nullable(),
    businessName:
      localStorage.getItem('selectedUserType') === 'BUSINESS'
        ? z.string().min(1, 'Please enter business name')
        : z.string().optional(),
    email: z
      .string({
        required_error: 'Please enter email',
      })
      .email('Invalid email format')
      .optional()
      .nullable()
      .or(z.literal('')),
    phone: z.string().regex(
      // /^((0|\+84)(3[2-9]|5[6|8|9]|7[0|6-9]|8[1-5]|9[0-9])[0-9]{7}|\+?[1-9]\d{1,14})$/,
      /^((0|\+84)(3[2-9]|5[6|8|9]|7[0|6-9]|8[1-6|8|9]|9[0-9])[0-9]{7}|\+?[1-9]\d{1,14})$/,
      'Please enter a valid phone number',
    ),
    parent: z.string().optional().nullable(),
    address: z.string().min(1, 'Please enter address'),
    idCard:
      // localStorage.getItem('selectedUserType') === 'BUSINESS'
      //   ? z.string().regex(/^[A-Z0-9]{6,20}$/, 'Please enter a valid ID number')
      //   :
      z.string().optional().nullable().or(z.literal('')),
    taxCode:
      localStorage.getItem('selectedUserType') === 'BUSINESS'
        ? z.string().min(1, 'Please enter tax code')
        : z.string().optional().nullable().or(z.literal('')),
    dob: z
      .string({
        required_error: 'Please enter date of birth',
      })
      .date('Please enter a valid date'),
    gender: z.enum(['MALE', 'FEMALE', 'UNDEFINED']).optional(),
  });
  type UserInfoInputType = z.infer<typeof UserInfoInput>;

  const [formErrors, setFormErrors] = React.useState<FormErrors>({});

  const handleConfirm = async (data: UserInfoInputType) => {
    try {
      if (user) {
        const updateRes = await apiPost<
          {
            status: number;
            message: string;
            data: User;
          },
          UserInfoInputType
        >('/user/update', data);
        notifyAPIClient(updateRes.message);
      }
      fetchUsers();
      handleClose();
    } catch (error) {
      notifyAPIClient(
        'An unexpected error occurred while processing your request.',
      );
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    try {
      const data = UserInfoInput.parse({
        id: user?.id,
        firstName: formData.get('firstName'),
        lastName: formData.get('lastName'),
        preferredName: formData.get('preferredName'),
        businessName: formData.get('businessName') || '',
        email: formData.get('email'),
        phone: formData.get('phone'),
        parent: formData.get('parent'),
        address: formData.get('address'),
        idCard: formData.get('idCard'),
        taxCode: formData.get('taxCode'),
        dob:
          formData.get('dob') ||
          new Date(user?.dob || '').toISOString().split('T')[0],
        gender: formData.get('gender') as Gender,
      });
      setFormErrors({});
      handleConfirm(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormErrors;
          fieldErrors[field] = err.message;
        });
        // console.error('Validation errors:', fieldErrors);
        setFormErrors(fieldErrors);
      } else {
        console.error(error);
        notifyAPIClient(
          'An unexpected error occurred while processing your request.',
        );
      }
    }
  };

  React.useEffect(() => {
    if (!open) {
      setFormErrors({});
      localStorage.removeItem('selectedUserType');
    }
  }, [open]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          maxHeight: 'calc(100vh - 32px)',
        },
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>
          {readOnly ? 'Client Profile' : 'Edit User Information'}
        </DialogTitle>
        <Tabs
          value={tab}
          onChange={(_event, value) => setTab(value)}
          sx={{ px: 3, flexShrink: 0 }}
        >
          <Tab value="profile" label="Profile" />
          <Tab value="services" label="Services" />
        </Tabs>
        <DialogContent sx={{ minHeight: 0, overflowY: 'auto' }}>
          {tab === 'services' ? (
            <ClientServicesTab userId={user?.id ?? null} />
          ) : (
          <fieldset
            disabled={readOnly}
            style={{
              border: 0,
              margin: 0,
              padding: 0,
              minInlineSize: 'auto',
            }}
          >
          <TextField
            margin="dense"
            id="firstName"
            name="firstName"
            className="required-field"
            label="First Name"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={user?.firstName || ''}
            error={Boolean(formErrors.firstName)}
            helperText={formErrors.firstName}
          />

          <TextField
            margin="dense"
            id="lastName"
            name="lastName"
            className="required-field"
            label="Last Name"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={user?.lastName || ''}
            error={Boolean(formErrors.lastName)}
            helperText={formErrors.lastName}
          />

          {user?.userType !== 'BUSINESS' ? (
            <>
              <TextField
                margin="dense"
                id="preferredName"
                name="preferredName"
                label="Preferred Name"
                type="text"
                fullWidth
                variant="standard"
                defaultValue={user?.preferredName || ''}
                error={Boolean(formErrors.preferredName)}
                helperText={formErrors.preferredName}
              />
              <TextField
                margin="dense"
                id="dob"
                name="dob"
                label="Date of Birth"
                type="date"
                fullWidth
                variant="standard"
                defaultValue={
                  // Date format
                  user?.dob
                    ? new Date(user.dob).toISOString().split('T')[0]
                    : ''
                }
                error={Boolean(formErrors.dob)}
                helperText={formErrors.dob}
              />
              <TextField
                margin="dense"
                id="parent"
                name="parent"
                label={'Parent Name'}
                type="text"
                fullWidth
                variant="standard"
                defaultValue={user?.parent || ''}
                error={Boolean(formErrors.parent)}
                helperText={formErrors.parent}
              />
            </>
          ) : (
            <TextField
              margin="dense"
              id="businessName"
              name="businessName"
              className="required-field"
              label="Business Name"
              type="text"
              fullWidth
              variant="standard"
              defaultValue={user?.businessName || ''}
              error={Boolean(formErrors.businessName)}
              helperText={formErrors.businessName}
            />
          )}

          {user?.userType !== 'BUSINESS' && (
            <FormControl
              fullWidth
              margin="dense"
              variant="standard"
              error={Boolean(formErrors.gender)}
            >
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select
                labelId="gender-label"
                id="gender"
                name="gender"
                disabled={readOnly}
                defaultValue={user?.gender || 'UNDEFINED'}
              >
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
                <MenuItem value="UNDEFINED">Undefined</MenuItem>
              </Select>
              {formErrors.gender && (
                <FormHelperText>{formErrors.gender}</FormHelperText>
              )}
            </FormControl>
          )}

          <TextField
            margin="dense"
            id="email"
            name="email"
            label="Email"
            type="email"
            fullWidth
            variant="standard"
            defaultValue={user?.email || ''}
            error={Boolean(formErrors.email)}
            helperText={formErrors.email}
          />

          <TextField
            margin="dense"
            id="phone"
            name="phone"
            className="required-field"
            label="Phone"
            type="tel"
            fullWidth
            variant="standard"
            defaultValue={user?.phone || ''}
            error={Boolean(formErrors.phone)}
            helperText={formErrors.phone}
          />

          <TextField
            margin="dense"
            id="address"
            name="address"
            className="required-field"
            label="Address"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={user?.address || ''}
            error={Boolean(formErrors.address)}
            helperText={formErrors.address}
          />

          <TextField
            margin="dense"
            id="idCard"
            name="idCard"
            label={
              localStorage.getItem('selectedUserType') === 'BUSINESS'
                ? 'Identity Number'
                : 'Identity Number'
            }
            type="text"
            fullWidth
            variant="standard"
            defaultValue={user?.idCard || ''}
            error={Boolean(formErrors.idCard)}
            helperText={formErrors.idCard}
          />

          <TextField
            margin="dense"
            id="taxCode"
            name="taxCode"
            className="required-field"
            label={
              localStorage.getItem('selectedUserType') === 'BUSINESS'
                ? 'Tax Code'
                : 'Tax Code'
            }
            type="text"
            fullWidth
            variant="standard"
            defaultValue={user?.taxCode || ''}
            error={Boolean(formErrors.taxCode)}
            helperText={formErrors.taxCode}
          />
          </fieldset>
          )}
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button onClick={handleClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
          {readOnly ? (
            <Button
              variant="contained"
              onClick={() => {
                setEditMode(true);
                setTab('profile');
              }}
            >
              Edit
            </Button>
          ) : (
            tab === 'profile' && <Button type="submit">Confirm</Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserInfoDialog;
